<script lang="ts" setup>
//#region Import

import { ref } from 'vue';

// Component
import IconVideoCamera from '@/shared/icons/IconVideoCamera.vue';
import ModalBadFile from '@/shared/component/modal-bad-file.vue';
import ModalNotify from '@/shared/component/modal-notify.vue';
import ModalWaitSaveFile from '@/shared/component/modal-wait-save-file.vue';

// Service
import { insertEtapaFotoVideoAdicional } from '@/service/vistoria-etapas';
import { saveVideo } from '@/service/vistoria-files';
import { useStepVistoria } from '@/stores/stepVistoria';

// Other
import {
  ID_MODELO_VIDEO_ADICIONAL,
  TIPO_ETAPA_VIDEO,
  MAX_VIDEO_FILE_BYTES,
  MAX_VIDEO_FILE_MB,
} from '@/global';
import { goVerificar } from '@/shared/utils/routeNavigate';

//#endregion

const vistoriaStore = useStepVistoria();

const inputElement = ref<HTMLInputElement>();
const showModal = ref<boolean>(false);
const showModalLoading = ref<boolean>(false);
const showVideoSizeWarning = ref<boolean>(false);
const videoSizeWarningMessage = ref<string>('');

/**
 * Fecha o modal
 */
const closeModal = () => {
  showModal.value = false;
};

const closeVideoSizeWarning = () => {
  showVideoSizeWarning.value = false;
};

const clearFileInput = () => {
  if (inputElement.value) {
    inputElement.value.value = '';
  }
};

/**
 * Validar o tipo de arquivo
 * @param type tipo do arquivo
 */
const validateFileType = (type: string) => {
  if (type.indexOf('video/') >= 0) {
    return true;
  }

  showModal.value = true;
  return false;
};

/**
 * Arquivo selecionado através da galeria
 */
const selectedFile = async () => {
  if (inputElement.value?.files) {
    const file: File = inputElement.value.files[0];

    if (file) {
      if (!validateFileType(file.type)) {
        clearFileInput();
        return;
      }

      showModalLoading.value = true;

      if (props.fotoAdicional) {
        await addVideoAdicional();
      }

      const type = file.type;

      if (file.size > MAX_VIDEO_FILE_BYTES) {
        showModalLoading.value = false;
        videoSizeWarningMessage.value = `O vídeo ultrapassa o limite de ${MAX_VIDEO_FILE_MB} MB. Use um arquivo menor ou grave um vídeo mais curto.`;
        showVideoSizeWarning.value = true;
        clearFileInput();
        return;
      }

      const value = new Blob([await file.arrayBuffer()]);

      const result = await saveVideo(
        vistoriaStore.currentVistoriaEtapa!.idEtapa,
        vistoriaStore.vistoria!.chave,
        [value],
        type,
      );

      if (!result.ok) {
        showModalLoading.value = false;
        videoSizeWarningMessage.value = result.warning;
        showVideoSizeWarning.value = true;
        clearFileInput();
        return;
      }

      clearFileInput();
      goVerificar();
    }
  }
};

const props = defineProps({
  fotoAdicional: {
    type: Boolean,
    default: false,
  },
});

/**
 * Adicona Video Adicional
 */
const addVideoAdicional = async () => {
  const idEtapa = await insertEtapaFotoVideoAdicional(
    vistoriaStore.vistoria!.chave,
    ID_MODELO_VIDEO_ADICIONAL,
  );
  vistoriaStore.setCurrentVistoriaEtapaRetry(idEtapa);
};
</script>

<template>
  <div
    v-if="
      vistoriaStore.currentVistoriaEtapa?.tipo === TIPO_ETAPA_VIDEO ||
      props.fotoAdicional
    "
  >
    <div
      :class="{
        'card buttonTakeGalleryFile': !props.fotoAdicional,
        'btn-vistoria': props.fotoAdicional,
      }"
      role="button"
    >
      <label for="input-video" v-if="!props.fotoAdicional">
        <span>Gravar Vídeo</span>
        <IconVideoCamera />
      </label>
      <label for="input-video" v-else class="btn">
        Incluir Vídeo Adicional
      </label>
      <input
        type="file"
        id="input-video"
        style="display: none"
        accept="video/*"
        ref="inputElement"
        capture="environment"
        @change="selectedFile()"
      />
    </div>

    <ModalBadFile :show="showModal" @close="closeModal()" />

    <ModalWaitSaveFile :show="showModalLoading" />

    <ModalNotify
      title="Atenção"
      :message="videoSizeWarningMessage"
      :show="showVideoSizeWarning"
      @close="closeVideoSizeWarning()"
    />
  </div>
</template>

<style lang="scss">
.buttonTakeGalleryFile {
  margin-top: 0.8rem;
  color: var(--primary);

  label {
    display: flex;
    align-items: center;
    flex-direction: row !important;
    justify-content: space-between;
    padding: 1rem;
    cursor: pointer;
  }

  span {
    font-weight: 500;
    display: block;
  }

  svg {
    display: block;
    width: 2rem;
    height: 2rem;
  }
}

.btn-vistoria {
  margin-top: 0.8rem;

  label {
    width: 100%;
    text-transform: uppercase;
    font-size: 0.7rem;
  }
}
</style>
